import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import { toast } from "sonner";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import { Check, ExternalLink, Loader2, Search, Sparkles } from "lucide-react";
import { themeFallbackClass } from "@/lib/storefrontTheme";
import { CarPartsAddonDialog, isCarPartsTheme, themeRequiresAddon, themeLabel } from "@/components/CarPartsOnboardDialog";

export default function MerchantThemeSetup() {
  const nav = useNavigate();
  const { refresh } = useMerchantAuth();
  const [sp, setSp] = useSearchParams();
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [requiredAddon, setRequiredAddon] = useState(null);
  const [requiredByTheme, setRequiredByTheme] = useState({});
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [addonOpen, setAddonOpen] = useState(false);
  const cfOrderId = sp.get("cf_order_id");
  const returnTheme = (sp.get("theme") || "").trim().toLowerCase();

  const loadAppearance = async () => {
    const { data } = await api.get("/merchant/appearance");
    setThemes(data.themes || []);
    setCategories(data.categories || []);
    setRequiredAddon(data.required_addon || null);
    setRequiredByTheme(data.required_addons || {});
    return data;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (cfOrderId) {
          setSaving(true);
          const { data: v } = await api.get(`/merchant/addons/verify/${cfOrderId}`);
          if (!alive) return;
          if (v.active) {
            const themeId = themeRequiresAddon(returnTheme) ? returnTheme : "carparts";
            try {
              await api.post("/merchant/appearance/setup", { theme_id: themeId });
              await refresh();
              toast.success(`${themeLabel(themeId)} addons unlocked — your storefront is ready`);
              nav("/admin", { replace: true });
              return;
            } catch (e) {
              toast.error(extractErr(e));
            }
          } else {
            toast.error("Payment not completed yet. If you paid, it will unlock shortly.");
          }
          const next = new URLSearchParams(sp);
          next.delete("cf_order_id");
          setSp(next, { replace: true });
        }
        await loadAppearance();
      } catch (e) {
        if (alive) toast.error(extractErr(e));
      } finally {
        if (alive) {
          setLoading(false);
          setSaving(false);
        }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfOrderId]);

  const catById = Object.fromEntries(categories.map((c) => [c.id, c]));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...themes];
    if (categoryId !== "all") {
      list = list.filter((t) => (t.category_ids || []).includes(categoryId));
    }
    if (q) {
      list = list.filter((t) => {
        const cats = (t.category_ids || []).map((id) => catById[id]?.name || id).join(" ");
        const hay = [t.id, t.label, t.description, (t.tags || []).join(" "), t.badge, cats].join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [themes, query, categoryId, catById]);

  const applyTheme = async (themeId) => {
    setSaving(true);
    try {
      await api.post("/merchant/appearance/setup", { theme_id: themeId });
      await refresh();
      toast.success("Your storefront is ready");
      nav("/admin", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d && typeof d === "object" && d.code === "addon_required") {
        setRequiredAddon({
          ...(requiredAddon || {}),
          id: d.addon_id,
          name: d.addon_name,
          price: d.price,
          addon_amount: d.price,
          gst_amount: d.gst_amount,
          gst_percent: d.gst_percent,
          total: d.total,
          owned: false,
          theme_id: d.theme_id,
        });
        setAddonOpen(true);
      } else {
        toast.error(extractErr(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const go = async () => {
    if (!picked) return;
    if (themeRequiresAddon(picked) && ((requiredByTheme[picked] && !requiredByTheme[picked].owned) || (isCarPartsTheme(picked) && requiredAddon && !requiredAddon.owned))) {
      setRequiredAddon(requiredByTheme[picked] || requiredAddon);
      setAddonOpen(true);
      return;
    }
    await applyTheme(picked);
  };

  const payAddon = async () => {
    if (!picked && !themeRequiresAddon(returnTheme)) return;
    const themeId = picked || returnTheme || "carparts";
    setSaving(true);
    try {
      const { data: d } = await api.post(`/merchant/addons/theme-required/checkout?theme_id=${encodeURIComponent(themeId)}&next=theme-setup`);
      const cashfree = await loadCashfree({ mode: d.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: d.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(extractErr(e));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Brand variant="dark" size="md" />
        <div className="mt-8 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-orange-200">
            <Sparkles className="w-3.5 h-3.5" /> Choose your look
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-5xl font-bold" data-testid="theme-setup-title">Pick a storefront theme</h1>
          <p className="mt-3 text-slate-400">This is how customers see your shop. You can switch later from Theme Store in the dashboard.</p>
        </div>

        {!loading ? (
          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="theme-setup-filters">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search themes by name, style, or industry"
                data-testid="theme-setup-search"
                className="h-12 w-full rounded-full border border-slate-700 bg-slate-900 pl-11 pr-4 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-orange-400"
              />
            </div>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              data-testid="theme-setup-category"
              className="h-12 rounded-full border border-slate-700 bg-slate-900 px-4 text-sm text-slate-100 outline-none focus:border-orange-400 sm:w-56"
            >
              <option value="all">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        ) : null}

        {loading ? (
          <div className="mt-16 flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading themes…</div>
        ) : (
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {filtered.map((th) => {
              const img = th.screenshot_url || th.thumbnail_url;
              const sel = picked === th.id;
              return (
                <button
                  key={th.id}
                  type="button"
                  onClick={() => setPicked(th.id)}
                  data-testid={`theme-setup-card-${th.id}`}
                  className={`text-left rounded-3xl overflow-hidden border-2 transition bg-slate-900 ${sel ? "border-orange-400 ring-2 ring-orange-400/40" : "border-slate-800 hover:border-slate-600"}`}
                >
                  <div className="relative bg-slate-800 h-[28rem] overflow-hidden">
                    {img ? (
                      <img src={mediaUrl(img)} alt={th.label} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className={`absolute inset-0 ${themeFallbackClass(th.id)}`} />
                    )}
                    {sel ? (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-orange-500 text-slate-950 text-xs font-bold px-2.5 py-1">
                        <Check className="w-3.5 h-3.5" /> Selected
                      </span>
                    ) : null}
                    {themeRequiresAddon(th.id) ? (
                      <span className="absolute top-3 left-3 rounded-full bg-slate-950/80 border border-orange-400/40 text-orange-200 text-[11px] font-bold px-2.5 py-1">Addon required</span>
                    ) : null}
                  </div>
                  <div className="p-5">
                    <div className="font-display text-2xl font-bold">{th.label}</div>
                    <p className="mt-1 text-sm text-slate-400">{th.description}</p>
                    {th.preview_url ? (
                      <a
                        href={th.preview_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-300 hover:text-orange-200"
                      >
                        Preview <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : null}
                  </div>
                </button>
              );
            })}
            {!filtered.length ? (
              <p className="md:col-span-2 text-slate-400 text-sm" data-testid="theme-setup-empty">No themes match that search. Try another keyword or category.</p>
            ) : null}
          </div>
        )}

        <div className="mt-8 flex justify-end">
          <Button
            type="button"
            disabled={!picked || saving}
            onClick={go}
            data-testid="theme-setup-continue"
            className="h-12 px-8 rounded-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</> : "Use this theme & go to dashboard"}
          </Button>
        </div>
      </div>
      <CarPartsAddonDialog
        open={addonOpen}
        onOpenChange={setAddonOpen}
        mode="setup"
        themeId={picked || returnTheme || "carparts"}
        quote={requiredAddon}
        busy={saving}
        onConfirm={payAddon}
        onSecondary={() => { setAddonOpen(false); setPicked(null); }}
      />
    </div>
  );
}
